import { useEffect, useReducer, useRef, useState } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import { Popover, PopoverHeader, PopoverBody, Input } from 'reactstrap'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import { RELATION_OPTIONS_REGISTRY } from './relationOptions'
import './AdvancedFilters.css'

export const AdvancedFilterFieldsQuery = gql`
    query AdvancedFilterFields($entity: String!, $context: GenericScalar) {
        filterFields(entity: $entity, context: $context) {
            fieldName
            label
            fieldKind
            options { value label }
            relationModel
            allowedFilterTypes { value label valuesRequired }
        }
    }
`

export const ADVANCED_FILTERS_TRIGGER_ID = 'advancedFiltersTrigger'

const SUPPORTED_FIELD_KINDS = new Set(['text', 'number', 'date', 'datetime', 'time', 'boolean', 'enum', 'relation'])

export const BOOLEAN_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
]

const SELECT_STYLES = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
}

// The field select's option text is colored per fieldKind (see
// formatFieldOptionLabel below). react-select's default focused/selected
// option backgrounds are also blue -- close enough to some of our fieldKind
// colors that the text can become unreadable against its own highlight. Force
// a neutral, always-light highlight here instead so colored text stays legible
// no matter which fieldKind it belongs to.
const FIELD_SELECT_STYLES = {
    ...SELECT_STYLES,
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#e9ecf1' : state.isFocused ? '#f5f6f8' : 'transparent',
        color: 'inherit',
    }),
}

// Purely decorative per-kind icon + color so the field list is easier to
// scan at a glance -- fields are dynamic (from the backend's filter field
// registry), so this keys off fieldKind rather than any specific field name.
// Exported so applied-filter chips can reuse the same color per field.
const FIELD_KIND_VISUALS = {
    text: { icon: 'fa-font', color: '#0B80F9' },
    number: { icon: 'fa-hashtag', color: '#6f42c1' },
    date: { icon: 'fa-calendar', color: '#d97706' },
    datetime: { icon: 'fa-calendar', color: '#d97706' },
    time: { icon: 'fa-clock-o', color: '#d97706' },
    boolean: { icon: 'fa-toggle-on', color: '#0f9d75' },
    enum: { icon: 'fa-circle-o', color: '#0f9d75' },
    relation: { icon: 'fa-link', color: '#d6336c' },
}
const DEFAULT_FIELD_VISUAL = { icon: 'fa-align-left', color: '#7a8699' }

export function fieldVisual(fieldKind) {
    return FIELD_KIND_VISUALS[fieldKind] || DEFAULT_FIELD_VISUAL
}

// react-select's formatOptionLabel context tells us whether we're rendering
// the open menu list (color the text too, for fast scanning) or the closed
// control's selected value (icon only -- text stays the normal dark color
// so the selected field still reads like a regular filled-in control).
function formatFieldOptionLabel(option, { context }) {
    const visual = fieldVisual(option.fieldKind)
    const inMenu = context === 'menu'
    return (
        <span className="advanced-filter-field-option">
            <i className={`fa ${visual.icon}`} style={{ color: visual.color }} aria-hidden="true" />
            <span style={inMenu ? { color: visual.color } : undefined}>{option.label}</span>
        </span>
    )
}

const EMPTY_ROW = { fieldName: '', filterType: '', valuesRequired: 'one', value: '' }

function inputTypeForFieldKind(fieldKind) {
    if (fieldKind === 'number') return 'number'
    if (fieldKind === 'date') return 'date'
    if (fieldKind === 'datetime') return 'datetime-local'
    if (fieldKind === 'time') return 'time'
    return 'text'
}

// The backend's valuesRequired is a GraphQL enum, which serializes as its
// member name (e.g. "LIST", "NONE") rather than the lowercase value assigned
// to it in Python -- normalize before comparing against 'one'/'two'/'list'/'none'.
export function normalizeValuesRequired(valuesRequired) {
    return (valuesRequired || 'one').toLowerCase()
}

function emptyValueForShape(valuesRequired) {
    if (valuesRequired === 'two') return ['', '']
    if (valuesRequired === 'list') return []
    return ''
}

function isRowComplete(row) {
    if (!row.fieldName || !row.filterType) return false
    if (row.valuesRequired === 'none') return true
    if (row.valuesRequired === 'two') {
        return Array.isArray(row.value) && row.value[0] !== '' && row.value[0] != null && row.value[1] !== '' && row.value[1] != null
    }
    if (row.valuesRequired === 'list') return Array.isArray(row.value) && row.value.length > 0
    return row.value !== '' && row.value != null
}

function toFilterInput(row) {
    return {
        fieldName: row.fieldName,
        filterType: row.filterType,
        filterValues: row.valuesRequired === 'none' ? null : row.value,
    }
}

function rowsFromActiveFilters(activeFilters, fieldsByName, nextId) {
    return (activeFilters || []).map((filter) => {
        const field = fieldsByName[filter.fieldName]
        const operator = field?.allowedFilterTypes?.find((o) => o.value === filter.filterType)
        const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
        return {
            id: nextId(),
            fieldName: filter.fieldName,
            filterType: filter.filterType,
            valuesRequired,
            value: filter.filterValues == null ? emptyValueForShape(valuesRequired) : filter.filterValues,
        }
    })
}

function rowsFromPresets(presets, fieldsByName, nextId) {
    return (presets || []).filter((preset) => preset.visible !== false).map((preset) => {
        const field = fieldsByName[preset.fieldName]
        const operator = field?.allowedFilterTypes?.find((o) => o.value === preset.filterType)
        const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
        return {
            id: nextId(),
            presetId: preset.id,
            editable: preset.editable,
            fieldName: preset.fieldName,
            filterType: preset.filterType,
            valuesRequired,
            value: preset.filterValues == null ? emptyValueForShape(valuesRequired) : preset.filterValues,
        }
    })
}

// The value control is always rendered (never omitted) so a row's shape
// doesn't jump around as you fill it in -- it's just disabled until it's
// actually usable: once a field AND an operator are chosen, and only if
// that operator needs a value at all (not for Is Null / Is Not Null).
function FilterValueInput({ field, row, onChange, apolloClient, labelCache, disabled = false }) {
    const hasOperator = !!row.filterType
    const needsValue = hasOperator && row.valuesRequired !== 'none'

    if (!field || !hasOperator) {
        return <Input type="text" aria-label="Filter value" className="advanced-filter-row__value-control" placeholder="Value" disabled />
    }

    if (!needsValue) {
        return <Input type="text" aria-label="Filter value" className="advanced-filter-row__value-control" placeholder="No value needed" disabled />
    }

    if (field.fieldKind === 'relation') {
        const registryEntry = RELATION_OPTIONS_REGISTRY[field.relationModel]
        if (!registryEntry) {
            // No search config is registered for this relation's model, so we
            // can't offer a search-select. Let the user type the related
            // record's id/pk directly -- the backend accepts either a raw pk
            // or a graphene global id (see decode_global_id_value in
            // utilities/filters.py).
            if (row.valuesRequired === 'list') {
                const text = Array.isArray(row.value) ? row.value.join(', ') : ''
                return (
                    <Input
                        type="text"
                        aria-label="Filter value"
                        className="advanced-filter-row__value-control"
                        placeholder="e.g. 42, 43"
                        defaultValue={text}
                        onBlur={(e) => onChange(e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
                        disabled={disabled}
                    />
                )
            }
            return (
                <Input
                    type="text"
                    aria-label="Filter value"
                    className="advanced-filter-row__value-control"
                    placeholder="Enter ID (e.g. 42)"
                    value={row.value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                />
            )
        }

        // Preloads the most recent 30 records on open (defaultOptions), and
        // re-queries the same list with the typed text as `searchTerm` as the
        // user keeps typing -- react-select debounces this call itself.
        const loadOptions = (inputValue) => apolloClient.query({
            query: registryEntry.query,
            variables: { searchTerm: inputValue || '' },
            fetchPolicy: 'network-only',
        }).then((result) => {
            const loadedOptions = registryEntry.getOptions(result.data)
            loadedOptions.forEach((option) => { labelCache.current[option.value] = option.label })
            return loadedOptions
        })

        // The filter only stores raw ids (what the backend needs) -- look up
        // each one's label from whatever's been loaded into the cache so far
        // (e.g. from a prior pick this session). If it was never loaded (a
        // filter applied in an earlier session), fall back to the raw id.
        if (row.valuesRequired === 'list') {
            const selected = (row.value || []).map((id) => ({ value: id, label: labelCache.current[id] || id }))
            return (
                <AsyncSelect
                    inputId={`advanced-filter-value-${row.id}`}
                    aria-label="Filter value"
                    isMulti
                    className="bos-custom-select advanced-filter-row__value-control"
                    classNamePrefix="bos-select"
                    loadOptions={loadOptions}
                    defaultOptions
                    value={selected}
                    onChange={(selectedOptions) => {
                        (selectedOptions || []).forEach((option) => { labelCache.current[option.value] = option.label })
                        onChange((selectedOptions || []).map((option) => option.value))
                    }}
                    placeholder="Search..."
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={SELECT_STYLES}
                    isDisabled={disabled}
                />
            )
        }

        const selected = row.value ? { value: row.value, label: labelCache.current[row.value] || row.value } : null
        return (
            <AsyncSelect
                inputId={`advanced-filter-value-${row.id}`}
                aria-label="Filter value"
                className="bos-custom-select advanced-filter-row__value-control"
                classNamePrefix="bos-select"
                loadOptions={loadOptions}
                defaultOptions
                value={selected}
                onChange={(option) => {
                    if (option) labelCache.current[option.value] = option.label
                    onChange(option ? option.value : '')
                }}
                placeholder="Search..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
                isDisabled={disabled}
            />
        )
    }

    if (row.valuesRequired === 'list' && field.fieldKind === 'enum') {
        const options = (field.options || []).map((o) => ({ value: o.value, label: o.label }))
        const selected = options.filter((o) => (row.value || []).includes(o.value))
        return (
            <Select
                inputId={`advanced-filter-value-${row.id}`}
                aria-label="Filter value"
                isMulti
                className="bos-custom-select advanced-filter-row__value-control"
                classNamePrefix="bos-select"
                options={options}
                value={selected}
                onChange={(selectedOptions) => onChange((selectedOptions || []).map((o) => o.value))}
                placeholder="Select values..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
                isDisabled={disabled}
            />
        )
    }

    if (row.valuesRequired === 'list') {
        const text = Array.isArray(row.value) ? row.value.join(', ') : ''
        return (
            <Input
                type="text"
                aria-label="Filter value"
                className="advanced-filter-row__value-control"
                placeholder="e.g. value1, value2"
                defaultValue={text}
                onBlur={(e) => onChange(e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
                disabled={disabled}
            />
        )
    }

    if (field.fieldKind === 'boolean') {
        const options = BOOLEAN_OPTIONS
        const selected = options.find((o) => o.value === row.value) || null
        return (
            <Select
                inputId={`advanced-filter-value-${row.id}`}
                aria-label="Filter value"
                className="bos-custom-select advanced-filter-row__value-control"
                classNamePrefix="bos-select"
                options={options}
                value={selected}
                onChange={(option) => onChange(option ? option.value : '')}
                placeholder="Value"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
                isDisabled={disabled}
            />
        )
    }

    if (field.fieldKind === 'enum') {
        const options = (field.options || []).map((o) => ({ value: o.value, label: o.label }))
        const selected = options.find((o) => o.value === row.value) || null
        return (
            <Select
                inputId={`advanced-filter-value-${row.id}`}
                aria-label="Filter value"
                className="bos-custom-select advanced-filter-row__value-control"
                classNamePrefix="bos-select"
                options={options}
                value={selected}
                onChange={(option) => onChange(option ? option.value : '')}
                placeholder="Value"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
                isDisabled={disabled}
            />
        )
    }

    const inputType = inputTypeForFieldKind(field.fieldKind)

    if (row.valuesRequired === 'two') {
        const [from, to] = Array.isArray(row.value) ? row.value : ['', '']
        return (
            <div className="advanced-filter-row__value-pair">
                <Input type={inputType} aria-label="Filter value from" placeholder="From" value={from || ''} onChange={(e) => onChange([e.target.value, to])} disabled={disabled} />
                <span className="advanced-filter-row__value-pair-sep">–</span>
                <Input type={inputType} aria-label="Filter value to" placeholder="To" value={to || ''} onChange={(e) => onChange([from, e.target.value])} disabled={disabled} />
            </div>
        )
    }

    return (
        <Input
            type={inputType}
            aria-label="Filter value"
            className="advanced-filter-row__value-control"
            placeholder="Value"
            value={row.value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
        />
    )
}

function FilterRow({ row, fields, selectableFields, onChangeField, onChangeOperator, onChangeValue, onRemove, canRemove, locked = false, isFirstRow, apolloClient, labelCache }) {
    const field = fields.find((f) => f.fieldName === row.fieldName)
    const operators = field?.allowedFilterTypes || []
    const fieldOptions = selectableFields.map((f) => ({ value: f.fieldName, label: f.label, fieldKind: f.fieldKind }))
    // A hydrated row may reference a field outside the selectable list --
    // resolve its selected option from the full metadata instead.
    const selectedFieldOption = row.fieldName
        ? { value: row.fieldName, label: field?.label || row.fieldName, fieldKind: field?.fieldKind }
        : null
    const operatorOptions = operators.map((o) => ({ value: o.value, label: o.label }))

    return (
        <div className="advanced-filter-row">
            <span className="advanced-filter-row__prefix">
                {/* Every row after the first ANDs together -- that's the only
                    combination the backend supports today, so it's shown as a
                    fixed label rather than a choice. */}
                {isFirstRow ? (
                    <span className="advanced-filter-row__where">Where</span>
                ) : (
                    <span className="advanced-filter-row__and">And</span>
                )}
            </span>
            <Select
                inputId={`advanced-filter-field-${row.id}`}
                aria-label="Filter field"
                className="bos-custom-select advanced-filter-row__field"
                classNamePrefix="bos-select"
                options={fieldOptions}
                value={selectedFieldOption}
                onChange={(option) => onChangeField(row.id, option ? option.value : '')}
                formatOptionLabel={formatFieldOptionLabel}
                placeholder="Field"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={FIELD_SELECT_STYLES}
                isDisabled={locked}
            />
            <Select
                inputId={`advanced-filter-operator-${row.id}`}
                aria-label="Filter operator"
                className="bos-custom-select advanced-filter-row__operator"
                classNamePrefix="bos-select"
                options={operatorOptions}
                value={operatorOptions.find((o) => o.value === row.filterType) || null}
                onChange={(option) => onChangeOperator(row.id, option ? option.value : '')}
                placeholder="Operator"
                isDisabled={locked || !field}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
            />
            <FilterValueInput field={field} row={row} onChange={(value) => onChangeValue(row.id, value)} apolloClient={apolloClient} labelCache={labelCache} disabled={locked} />
            {locked ? (
                <i className="fa fa-lock advanced-filter-row__lock" title="Set by this page" aria-hidden="true" />
            ) : (canRemove && (
                <button type="button" className="advanced-filter-row__remove" onClick={() => onRemove(row.id)} aria-label="Remove filter">
                    <i className="fa fa-times" aria-hidden="true" />
                </button>
            ))}
        </div>
    )
}

// Generic advanced-filter builder popover -- pass `entity` (matching a
// backend-registered FilterSet, e.g. "task") and whatever `context` shape
// that entity's FilterSet expects (e.g. { boardId } for "task") to reuse this
// across any list view.
function AdvancedFilters({ isOpen, onClose, entity, context, activeFilters, onApply, visibleFields, hiddenFields, presets = [], onPresetsChange = () => {} }) {
    const nextIdRef = useRef(0)
    const nextId = () => {
        nextIdRef.current += 1
        return nextIdRef.current
    }
    const [rows, setRows] = useState([])
    const [presetRows, setPresetRows] = useState([])
    const [hydrated, setHydrated] = useState(false)
    const popoverRef = useRef(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    const handleApplyRef = useRef(() => {})
    const apolloClient = useApolloClient()
    // Caches id -> label for relation values picked (or loaded) this popover
    // session, since the filter itself only stores the raw id. Mutating a ref
    // doesn't trigger a re-render, so resolving a label asynchronously (see
    // the hydration effect below) also bumps this to force one.
    const relationLabelCache = useRef({})
    const [, bumpRelationLabels] = useReducer((c) => c + 1, 0)

    const { data, loading } = useQuery(AdvancedFilterFieldsQuery, {
        variables: { entity, context },
        skip: !isOpen,
        fetchPolicy: 'cache-and-network',
    })

    // Every supported field kind is selectable. A relation field whose
    // relationModel has a registered search config (see relationOptions.js)
    // gets an async search-select; one without falls back to a manual id/pk
    // text input (the backend resolves a raw pk or a global id alike).
    const fields = (data?.filterFields || []).filter((f) => SUPPORTED_FIELD_KINDS.has(f.fieldKind))

    // visibleFields/hiddenFields restrict what a user can PICK on this page.
    let selectableFields = fields
    if (visibleFields) selectableFields = selectableFields.filter((f) => visibleFields.includes(f.fieldName))
    if (hiddenFields) selectableFields = selectableFields.filter((f) => !hiddenFields.includes(f.fieldName))

    // A field is restricted here because it cannot mean anything on this view --
    // a kanban's grouping field, whose filter the page also strips from the
    // query. So nothing on that field gets a row -- neither a user filter nor a
    // preset: a row would claim a filter that is not being applied and has no
    // chip to match.
    //
    // Nothing is dropped, though. Both are held aside and written back on Apply
    // (the same treatment invisible presets get below), so they survive editing
    // here and apply again on a view that can honour them.
    const isFieldRestricted = (fieldName) =>
        !!(hiddenFields && hiddenFields.includes(fieldName))
        || !!(visibleFields && !visibleFields.includes(fieldName))
    const restrictedActiveFilters = useRef([])

    const fieldsByName = fields.reduce((acc, f) => { acc[f.fieldName] = f; return acc }, {})

    useEffect(() => {
        if (!isOpen) {
            setHydrated(false)
            return
        }
        if (hydrated || !data?.filterFields) return
        restrictedActiveFilters.current = (activeFilters || []).filter((f) => isFieldRestricted(f.fieldName))
        const shownActiveFilters = (activeFilters || []).filter((f) => !isFieldRestricted(f.fieldName))
        const hydratedRows = rowsFromActiveFilters(shownActiveFilters, fieldsByName, nextId)
        // Show one ready-to-fill row by default instead of an empty state.
        setRows(hydratedRows.length > 0 ? hydratedRows : [{ id: nextId(), ...EMPTY_ROW }])
        setPresetRows(rowsFromPresets(presets.filter((p) => !isFieldRestricted(p.fieldName)), fieldsByName, nextId))
        setHydrated(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, hydrated, data])

    // Rows hydrated from activeFilters carry relation values as raw ids with no
    // label (a fresh pick caches its label immediately via onChange, but a
    // filter that was applied in an earlier popover session has nothing cached
    // yet) -- resolve each one now so the value select shows the real label
    // instead of the id. Runs once right after hydration, not on every edit.
    useEffect(() => {
        if (!hydrated) return
        rows.forEach((row) => {
            const field = fieldsByName[row.fieldName]
            if (field?.fieldKind !== 'relation') return
            const registryEntry = RELATION_OPTIONS_REGISTRY[field.relationModel]
            if (!registryEntry) return
            const ids = Array.isArray(row.value) ? row.value : (row.value ? [row.value] : [])
            ids.forEach((id) => {
                if (!id || relationLabelCache.current[id] !== undefined) return
                relationLabelCache.current[id] = null
                registryEntry.resolveById(apolloClient, id).then((option) => {
                    if (option) relationLabelCache.current[id] = option.label
                    bumpRelationLabels()
                })
            })
        })
        presetRows.forEach((row) => {
            const field = fieldsByName[row.fieldName]
            if (field?.fieldKind !== 'relation') return
            const registryEntry = RELATION_OPTIONS_REGISTRY[field.relationModel]
            if (!registryEntry) return
            const ids = Array.isArray(row.value) ? row.value : (row.value ? [row.value] : [])
            ids.forEach((id) => {
                if (!id || relationLabelCache.current[id] !== undefined) return
                relationLabelCache.current[id] = null
                registryEntry.resolveById(apolloClient, id).then((option) => {
                    if (option) relationLabelCache.current[id] = option.label
                    bumpRelationLabels()
                })
            })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hydrated])

    // Reactstrap's own "legacy"/"click" trigger wiring doesn't know about
    // react-select's portaled dropdown menu (rendered outside the popover's
    // DOM subtree) and closes the popover the moment an option is clicked.
    // We run the popover fully "manual" and own open/close/outside-click/
    // escape ourselves, since we already have to account for the portal.
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onCloseRef.current()
                return
            }
            if (event.key === 'Enter') {
                // react-select preventDefaults the Enter presses it consumes
                // (picking the highlighted option) -- those aren't ours to handle.
                if (event.defaultPrevented) return
                if (!popoverRef.current?.contains(event.target)) return
                event.preventDefault()
                // Commit any in-progress text first (the comma-separated list input
                // parses on blur), then apply on the next tick so the state update
                // from that blur has landed before handleApply reads `rows`.
                if (event.target?.blur) {
                    // Ensure the element is focused before blurring to guarantee the blur event fires
                    if (document.activeElement !== event.target) {
                        event.target.focus?.()
                    }
                    event.target.blur()
                }
                setTimeout(() => handleApplyRef.current(), 0)
            }
        }
        const handleMouseDown = (event) => {
            const target = event.target
            const insidePopover = !!popoverRef.current?.contains(target)
            const inSelectMenu = !!(target.closest?.('.bos-select__menu') || target.closest?.('.bos-select__menu-portal'))
            const onTrigger = !!target.closest?.(`#${ADVANCED_FILTERS_TRIGGER_ID}`)
            if (!insidePopover && !inSelectMenu && !onTrigger) {
                onCloseRef.current()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleMouseDown, true)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handleMouseDown, true)
        }
    }, [isOpen])

    const addRow = () => {
        setRows((prev) => [...prev, { id: nextId(), ...EMPTY_ROW }])
    }

    const removeRow = (id) => {
        setRows((prev) => prev.filter((r) => r.id !== id))
    }

    const changeField = (id, fieldName) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { id: r.id, ...EMPTY_ROW, fieldName } : r)))
    }

    const changeOperator = (id, filterType) => {
        setRows((prev) => prev.map((r) => {
            if (r.id !== id) return r
            const field = fieldsByName[r.fieldName]
            const operator = field?.allowedFilterTypes?.find((o) => o.value === filterType)
            const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
            const sameShape = valuesRequired === r.valuesRequired
            return { ...r, filterType, valuesRequired, value: sameShape ? r.value : emptyValueForShape(valuesRequired) }
        }))
    }

    const changeValue = (id, value) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)))
    }

    const changePresetField = (id, fieldName) => {
        setPresetRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...EMPTY_ROW, id: r.id, presetId: r.presetId, editable: r.editable, fieldName } : r)))
    }
    const changePresetOperator = (id, filterType) => {
        setPresetRows((prev) => prev.map((r) => {
            if (r.id !== id) return r
            const field = fieldsByName[r.fieldName]
            const operator = field?.allowedFilterTypes?.find((o) => o.value === filterType)
            const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
            const sameShape = valuesRequired === r.valuesRequired
            return { ...r, filterType, valuesRequired, value: sameShape ? r.value : emptyValueForShape(valuesRequired) }
        }))
    }
    const changePresetValue = (id, value) => {
        setPresetRows((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)))
    }
    const removePresetRow = (id) => {
        setPresetRows((prev) => prev.filter((r) => r.id !== id))
    }

    const clearAll = () => {
        setRows([{ id: nextId(), ...EMPTY_ROW }])
        // Clear all means all, including the restricted filters held aside above
        // -- otherwise one survives every clear and silently reappears in list view.
        restrictedActiveFilters.current = []
        // Locked presets are page policy -- Clear all only drops the editable ones.
        setPresetRows((prev) => prev.filter((r) => !r.editable))
    }

    const handleApply = () => {
        // Presets that never got a row: the invisible ones (page policy, e.g.
        // for_user on My Tasks) and the restricted ones (this view's kanban
        // grouping field). One list, so a preset that is both is carried once.
        const carriedPresets = presets.filter(
            (preset) => preset.visible === false || isFieldRestricted(preset.fieldName),
        )
        onPresetsChange(
            presetRows.filter((row) => !row.editable || isRowComplete(row)).map((row) => ({
                id: row.presetId,
                fieldName: row.fieldName,
                filterType: row.filterType,
                filterValues: row.valuesRequired === 'none' ? null : row.value,
                editable: row.editable,
                visible: true,
            })).concat(carriedPresets)
        )
        onApply(rows.filter(isRowComplete).map(toFilterInput).concat(restrictedActiveFilters.current))
        onClose()
    }
    handleApplyRef.current = handleApply

    if (!isOpen) {
        return null
    }

    const totalRows = presetRows.length + rows.length
    const readyCount = presetRows.filter(isRowComplete).length + rows.filter(isRowComplete).length

    return (
        <Popover
            trigger="manual"
            isOpen={isOpen}
            target={ADVANCED_FILTERS_TRIGGER_ID}
            placement="bottom-end"
            className="advanced-filters-popover"
            innerRef={popoverRef}
        >
            <PopoverHeader>
                <div className="advanced-filters-header-left">
                    <span className="advanced-filters-header-icon"><i className="fa fa-sliders" aria-hidden="true" /></span>
                    <div>
                        <div className="advanced-filters-title">Advanced Filters</div>
                        <div className="advanced-filters-subtitle">{totalRows} active condition{totalRows === 1 ? '' : 's'}</div>
                    </div>
                </div>
                <div className="advanced-filters-header-right">
                    <a className="advanced-filters-header-clear" onClick={clearAll}>Clear all</a>
                    <button type="button" className="advanced-filters-header-apply" onClick={handleApply}>
                        <i className="fa fa-check" aria-hidden="true" /> Apply
                    </button>
                </div>
            </PopoverHeader>
            <PopoverBody>
                {loading && !data ? (
                    <div className="text-center py-2"><i className="fa fa-spinner fa-spin" /></div>
                ) : (
                    <>
                        <div className="advanced-filter-columns">
                            <span />
                            <span className="advanced-filter-columns__label">Field</span>
                            <span className="advanced-filter-columns__label">Operator</span>
                            <span className="advanced-filter-columns__label">Value</span>
                            <span />
                        </div>
                        {presetRows.map((row, index) => (
                            <FilterRow
                                key={row.id}
                                row={row}
                                fields={fields}
                                selectableFields={selectableFields}
                                onChangeField={changePresetField}
                                onChangeOperator={changePresetOperator}
                                onChangeValue={changePresetValue}
                                onRemove={removePresetRow}
                                canRemove={row.editable}
                                locked={!row.editable}
                                isFirstRow={index === 0}
                                apolloClient={apolloClient}
                                labelCache={relationLabelCache}
                            />
                        ))}
                        {rows.map((row, index) => (
                            <FilterRow
                                key={row.id}
                                row={row}
                                fields={fields}
                                selectableFields={selectableFields}
                                onChangeField={changeField}
                                onChangeOperator={changeOperator}
                                onChangeValue={changeValue}
                                onRemove={removeRow}
                                canRemove={rows.length > 1}
                                isFirstRow={presetRows.length === 0 && index === 0}
                                apolloClient={apolloClient}
                                labelCache={relationLabelCache}
                            />
                        ))}
                        <div className="advanced-filters-footer">
                            <a className="advanced-filters-add" onClick={addRow}>
                                <i className="fa fa-plus" aria-hidden="true" /> Add condition
                            </a>
                            <span className="advanced-filters-ready-count">{readyCount} of {totalRows} ready</span>
                        </div>
                    </>
                )}
            </PopoverBody>
        </Popover>
    )
}

export default AdvancedFilters
